import { environment } from '../../environments/environment';
import { switchMap, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, Subject, from } from 'rxjs';
import { Platform } from '@ionic/angular/standalone';
import { SentryService } from './sentry.service';
import { StorageService } from './storage.service';
import { HttpClient } from '@angular/common/http';
import { VersionMessage, VersionInfoApiResponse } from '../models/version-message';
import { Initializable } from './app-init.service';

@Injectable({ providedIn: 'root' })
export class MessageInfoVersionService implements Initializable {
  versionMessages: VersionMessage[] = [];
  versionMessagesObs = new Subject<VersionMessage>();
  versionMessagesObs$ = this.versionMessagesObs.asObservable();
  lastVersion: number[];
  versionString = require('../../../package.json').version;

  constructor(
    private platform: Platform,
    private http: HttpClient,
    private sentryService: SentryService,
    private storageService: StorageService
  ) {}

  async init() {
    const version = await this.fetchVersion();
    this.lastVersion = this.extractVersion(version);

    // Only fetch and show version messages on mobile platforms (iOS/Android)
    // AND when not running inside an iframe
    if (this.platform.is('cordova') && !this.isRunningInIframe()) {
      await this.fetchVersionMessages(this.versionString).toPromise();
    }
  }

  private isRunningInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // assume we're in an iframe for safety
      return true;
    }
  }

  fetchVersionMessages(version): Observable<any[]> {
    const platform = this.platform.is('ios') ? 'ios' : this.platform.is('android') ? 'android' : 'desktop';
    const url = `${environment.cdrBase}/v${environment.apiVersion}/versions/info?version=${version}&platform=${platform}`;
    return this.http.get<VersionInfoApiResponse>(url).pipe(
      switchMap((data) => from(this.processVersionMessages(data))),
      catchError((err) => {
        this.sentryService.sendToSentry(`SoftDeploy: Fail to fetch version messages ${JSON.stringify(err)}`);
        return of(null);
      })
    );
  }

  private async processVersionMessages(data: VersionInfoApiResponse): Promise<any[]> {
    if (data?.data) {
      const dataMessages = data.data;
      for (const dataMessage of dataMessages) {
        if (dataMessage?.id && dataMessage?.attributes?.action && dataMessage?.attributes?.message) {
          const messageId = dataMessage.id;
          const forceUpdate = dataMessage.attributes.force_update;

          // Check if message has been seen (only for non-force_update messages)
          const hasBeenSeen = await this.hasMessageBeenSeen(messageId);

          // Show message if:
          // 1. It's a force_update message (always show until user updates)
          // 2. It hasn't been seen yet
          if (forceUpdate || !hasBeenSeen) {
            const message: VersionMessage = {
              id: messageId,
              message: dataMessage.attributes.message,
              action: dataMessage.attributes.action,
              force_update: forceUpdate,
              cta_name: dataMessage.attributes.cta_name,
            };
            this.versionMessages.push(message);
            this.versionMessagesObs.next(message);
          }
        }
      }
    }
    return [];
  }

  async hasMessageBeenSeen(messageId: string): Promise<boolean> {
    const storageKey = `version_message_seen_${messageId}`;
    const seen = await this.storageService.get(storageKey);
    return !!seen;
  }

  async markMessageAsSeen(messageId: string): Promise<void> {
    const storageKey = `version_message_seen_${messageId}`;
    await this.storageService.set(storageKey, true);
  }

  fetchVersion(): Promise<any> {
    const url = `${environment.cdrBase}/v${environment.apiVersion}/versions/last_version`;
    return this.http
      .get(url)
      .pipe(
        switchMap((res: any) => {
          const version = !!res && !!res.data ? res.data.attributes.number : null;
          return of(version);
        }),
        catchError((err) => {
          this.sentryService.sendToSentry(`SoftDeploy: Fail to fetch version ${JSON.stringify(err)}`);
          return of(null);
        })
      )
      .toPromise();
  }

  extractVersion(vers: string): number[] {
    let array = null;
    if (!!vers) {
      array = vers.split('.').map((elt) => +elt);
    }
    return array;
  }
}
